<template>
  <div class="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
          Métodos de Pago Personalizados
        </h4>
        <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
          Agrega hasta 3 métodos de pago personalizados
        </p>
      </div>
      <button
        v-if="customMethods.length < 3"
        :disabled="isSaving"
        class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        @click="showDialog = true"
      >
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
          />
        </svg>
        Agregar Método
      </button>
    </div>

    <div
      v-if="customMethods.length > 0"
      class="space-y-2"
    >
      <div
        v-for="method in customMethods"
        :key="method.id"
        class="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <div class="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
            <svg
              class="w-5 h-5 text-purple-600 dark:text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-gray-900 dark:text-white truncate">
              {{ method.name }}
            </span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            :disabled="isSaving"
            :class="[
              'relative inline-flex h-5 w-9 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
              method.enabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600',
              isSaving ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            @click="emit('toggle', method.name)"
          >
            <span
              :class="[
                'pointer-events-none inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
                method.enabled ? 'translate-x-4 sm:translate-x-5' : 'translate-x-0'
              ]"
            />
          </button>

          <button
            :disabled="isSaving"
            class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
            @click="editCustomMethod(method)"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>

          <button
            :disabled="isSaving"
            class="p-1.5 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
            @click="emit('remove', method.name)"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <EmptyState
      v-else
      icon="folder"
      title="No hay métodos de pago personalizados"
      description="Agrega métodos personalizados para aceptar otras formas de pago"
      variant="card"
      size="md"
    />
  </div>

  <BaseModal
    :show="showDialog || !!editingCustomMethod"
    size="sm"
    :title="editingCustomMethod ? 'Editar Método de Pago' : 'Agregar Método de Pago'"
    :show-actions="true"
    :is-editing="!!editingCustomMethod"
    :disabled="!customMethodName.trim() || isSaving"
    :loading="isSaving"
    loading-text="Guardando..."
    save-text="Guardar"
    save-class="bg-purple-600 hover:bg-purple-700"
    @close="cancelDialog"
    @save="saveCustomMethod"
  >
    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Nombre del Método
        </label>
        <input
          ref="customMethodInput"
          v-model="customMethodName"
          type="text"
          placeholder="Ej: Credito, Familia, etc."
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          @keyup.enter="saveCustomMethod"
        >
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Máximo 3 métodos personalizados permitidos
        </p>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import type { CustomPaymentMethod } from '@/composables/useRestaurantSettings'

const props = defineProps<{
  isSaving: boolean
  customMethods: CustomPaymentMethod[]
}>()

const emit = defineEmits<{
  (e: 'toggle', methodName: string): void
  (e: 'remove', methodName: string): void
  (e: 'add', methodName: string): void
  (e: 'update', payload: { oldName: string; newName: string }): void
}>()

const showDialog = ref(false)
const editingCustomMethod = ref<CustomPaymentMethod | null>(null)
const customMethodName = ref('')
const customMethodInput = ref<HTMLInputElement>()

const editCustomMethod = (method: CustomPaymentMethod) => {
  editingCustomMethod.value = method
  customMethodName.value = method.name

  nextTick(() => {
    customMethodInput.value?.focus()
  })
}

const cancelDialog = () => {
  showDialog.value = false
  editingCustomMethod.value = null
  customMethodName.value = ''
}

const saveCustomMethod = () => {
  const newName = customMethodName.value.trim()
  if (!newName) return

  if (editingCustomMethod.value) {
    emit('update', { oldName: editingCustomMethod.value.name, newName })
  } else {
    emit('add', newName)
  }

  cancelDialog()
}
</script>
